import {
  Combobox,
  InputBase,
  useCombobox,
  ScrollArea,
  Loader,
  Text,
  Group,
  Box,
  useMantineTheme,
  CloseButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useState, forwardRef, useEffect } from 'react';
import { IconSearch, IconChevronDown } from '@tabler/icons-react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface SearchableSelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  group?: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  searchLoading?: boolean;
  label?: string;
  placeholder?: string;
  description?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  creatable?: boolean;
  onCreate?: (query: string) => void;
  createLabel?: (query: string) => string;
  nothingFoundMessage?: string;
  maxDropdownHeight?: number;
  debounceMs?: number;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  renderOption?: (option: SearchableSelectOption) => React.ReactNode;
}

const SearchableSelect = forwardRef<HTMLInputElement, SearchableSelectProps>(
  (
    {
      options,
      value,
      onChange,
      onSearch,
      onLoadMore,
      hasMore,
      loading = false,
      searchLoading = false,
      label,
      placeholder = 'Search...',
      description,
      error,
      required,
      disabled,
      clearable = true,
      searchable = true,
      creatable = false,
      onCreate,
      createLabel = (query) => `Create "${query}"`,
      nothingFoundMessage = 'No options found',
      maxDropdownHeight = 250,
      debounceMs = 300,
      gradientVariant = 'primary',
      renderOption,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const combobox = useCombobox({
      onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, debounceMs);

    const selectedOption = options.find((opt) => opt.value === value);

    useEffect(() => {
      if (onSearch) {
        onSearch(debouncedSearch);
      }
    }, [debouncedSearch, onSearch]);

    const filteredOptions = onSearch
      ? options
      : options.filter((opt) =>
          opt.label.toLowerCase().includes(search.toLowerCase()) ||
          opt.description?.toLowerCase().includes(search.toLowerCase())
        );

    const groupedOptions = filteredOptions.reduce((acc, option) => {
      const group = option.group || '';
      if (!acc[group]) acc[group] = [];
      acc[group].push(option);
      return acc;
    }, {} as Record<string, SearchableSelectOption[]>);

    const handleOptionSelect = (val: string) => {
      if (val === '__create__') {
        onCreate?.(search);
      } else {
        onChange?.(val);
        setSearch('');
      }
      combobox.closeDropdown();
    };

    const handleClear = () => {
      onChange?.(null);
      setSearch('');
    };

    const renderOptionItem = (option: SearchableSelectOption) => {
      if (renderOption) {
        return renderOption(option);
      }

      return (
        <Group gap="sm">
          {option.icon}
          <Box>
            <Text size="sm" style={accessibilityStyles}>{option.label}</Text>
            {option.description && (
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                {option.description}
              </Text>
            )}
          </Box>
        </Group>
      );
    };

    const optionElements = Object.entries(groupedOptions).map(([group, groupOptions]) => (
      <Box key={group || 'ungrouped'}>
        {group && (
          <Combobox.Group label={group}>
            {groupOptions.map((option) => (
              <Combobox.Option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                active={option.value === value}
              >
                {renderOptionItem(option)}
              </Combobox.Option>
            ))}
          </Combobox.Group>
        )}
        {!group &&
          groupOptions.map((option) => (
            <Combobox.Option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              active={option.value === value}
            >
              {renderOptionItem(option)}
            </Combobox.Option>
          ))}
      </Box>
    ));

    const showCreate = creatable && search.length > 0 && !options.some((opt) => opt.label.toLowerCase() === search.toLowerCase());
    const showNothing = filteredOptions.length === 0 && !showCreate && !searchLoading;

    return (
      <Combobox
        store={combobox}
        onOptionSubmit={handleOptionSelect}
        withinPortal={true}
      >
        <Combobox.Target>
          <InputBase
            ref={ref}
            label={label}
            description={description}
            error={error}
            required={required}
            disabled={disabled}
            pointer={!searchable}
            rightSection={
              loading ? (
                <Loader size={16} />
              ) : clearable && value ? (
                <CloseButton
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                />
              ) : (
                <IconChevronDown size={16} />
              )
            }
            rightSectionPointerEvents={clearable && value ? 'all' : 'none'}
            leftSection={searchable ? <IconSearch size={16} /> : undefined}
            value={searchable ? (combobox.dropdownOpened ? search : selectedOption?.label || '') : selectedOption?.label || ''}
            onChange={(event) => {
              if (searchable) {
                setSearch(event.currentTarget.value);
                combobox.openDropdown();
                combobox.updateSelectedOptionIndex();
              }
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => {
              combobox.closeDropdown();
              setSearch('');
            }}
            placeholder={placeholder}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            <ScrollArea.Autosize mah={maxDropdownHeight} type="scroll">
              {searchLoading && (
                <Combobox.Empty>
                  <Group justify="center" py="xs">
                    <Loader size="sm" />
                    <Text size="sm" style={accessibilityStyles}>Searching...</Text>
                  </Group>
                </Combobox.Empty>
              )}

              {!searchLoading && optionElements}

              {showCreate && (
                <Combobox.Option value="__create__">
                  <Text size="sm" c={gradient?.from} style={accessibilityStyles}>
                    {createLabel(search)}
                  </Text>
                </Combobox.Option>
              )}

              {showNothing && (
                <Combobox.Empty>{nothingFoundMessage}</Combobox.Empty>
              )}

              {hasMore && !searchLoading && (
                <Box
                  py="xs"
                  px="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onLoadMore?.()}
                >
                  <Text size="sm" c="dimmed" ta="center" style={accessibilityStyles}>
                    Load more...
                  </Text>
                </Box>
              )}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    );
  }
);

SearchableSelect.displayName = 'SearchableSelect';

export default SearchableSelect;
